import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faShieldAlt,
  faCalendarAlt,
  faEnvelope,
  faPhone,
  faRefresh,
  faTimeline,
  faInfoCircle,
  faUser,
  faBuilding,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { InlineSpinner } from '../../componentes/LoadingIndicator';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import PageHeader from '../../componentes/PageHeader';
import GradientBanner from '../../componentes/Banner';
import { API_BASE_URL } from '../../api/config';

const TrackReport = () => {
  const { t } = useTranslation();
  const [trackingId, setTrackingId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState('');

  // Load saved data on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('trackingData');
    if (savedData) {
      const { trackingId: savedTrackingId, reportData: savedReportData, timestamp } = JSON.parse(savedData);

      // Check if data is less than 24 hours old
      const now = new Date().getTime();
      const oneDay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

      if (timestamp && (now - timestamp) < oneDay) {
        setTrackingId(savedTrackingId || '');
        setReportData(savedReportData || null);
      } else {
        // Data is older than 24 hours, remove it
        localStorage.removeItem('trackingData');
      }
    }
  }, []);

  // Save data whenever reportData changes
  useEffect(() => {
    if (reportData) {
      localStorage.setItem('trackingData', JSON.stringify({
        trackingId,
        reportData,
        timestamp: new Date().getTime()
      }));
    }
  }, [reportData, trackingId]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const cleanTrackingId = trackingId.trim();
    if (!cleanTrackingId) {
      toast.error(t('track_report.error_enter_tracking_id'));
      return;
    }

    // Add format validation
    const trackingIdRegex = /^[A-Z0-9-]+$/;
    if (!trackingIdRegex.test(cleanTrackingId)) {
      toast.error(t('track_report.error_invalid_format'));
      return;
    }

    // Add length validation
    if (cleanTrackingId.length > 20) {
      toast.error(t('track_report.error_too_long'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/incident-reports/track/${cleanTrackingId}/`
      );
      setReportData(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setError(t('track_report.error_not_found'));
      } else {
        setError(t('track_report.error_general'));
      }
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    const cleanTrackingId = trackingId.trim();
    if (!cleanTrackingId) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/incident-reports/track/${cleanTrackingId}/`
      );
      setReportData(response.data);
      toast.success(t('track_report.success_refresh'));
    } catch (error) {
      if (error.response?.status === 404) {
        setError(t('track_report.error_not_found'));
      } else {
        setError(t('track_report.error_refresh'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getReportTypeIcon = (type) => {
    if (type.toLowerCase().includes('personal')) return faUser;
    if (type.toLowerCase().includes('organization')) return faBuilding;
    if (type.toLowerCase().includes('vulnerability')) return faShieldAlt;
    return faExclamationTriangle;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDisplayStatus = (status) => {
    if (status === 'Requires Action') return t('track_report.display_status_attempt');
    return status;
  };

  const getTimelineSteps = (status, submittedDate, updatedDate) => {
    const finalStatuses = ['resolved', 'closed', 'requires_action', 'reject', 'completed'];
    const steps = [
      { label: t('track_report.status_submitted'), status: 'completed', date: submittedDate },
      { label: t('track_report.status_pending'), status: 'completed', date: submittedDate },
      { label: t('track_report.status_in_progress'), status: status === 'in_progress' ? 'current' : (status === 'pending' ? 'pending' : 'completed'), date: status !== 'pending' ? updatedDate : null },
      { label: t('track_report.status_closed'), status: finalStatuses.includes(status) ? 'completed' : 'pending', date: finalStatuses.includes(status) ? updatedDate : null }
    ];
    return steps;
  };

  return (
    <>
      <PageHeader />
      <GradientBanner
        title={t("track_report.title")}
        description={t("track_report.subtitle")}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("track_report.breadcrumb"), link: null },
        ]}
        backgroundIcons={[faSearch, faShieldAlt, faTimeline, faCheckCircle]}
      />

      <div className="min-h-screen bg-gray-50 py-8 font-nunito-sans">
        <div className="px-dynamic">
          {/* Search Section */}
          {!reportData && (
            <div className="max-w-2xl mx-auto mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-lg p-8"
              >
                <div className="text-center mb-6">
                  <FontAwesomeIcon icon={faSearch} className="text-4xl text-primary mb-4" />
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">{t("track_report.search_title")}</h2>
                  <p className="text-gray-600">{t("track_report.search_description")}</p>
                </div>

                <form onSubmit={handleSearch} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("track_report.tracking_id_label")}
                    </label>
                    <input
                      type="text"
                      value={trackingId}
                      onChange={(e) => setTrackingId(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-primary text-white py-3 px-6 rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isLoading ? (
                      <>
                        <InlineSpinner size="sm" className="mr-2 border-white/30 border-t-white" />
                        {t("track_report.searching")}
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSearch} className="mr-2" />
                        {t("track_report.track_button")}
                      </>
                    )}
                  </button>
                </form>

                {error && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faExclamationCircle} className="text-red-500 mr-2" />
                      <span className="text-red-700">{error}</span>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          )}

          {/* Results Dashboard */}
          {reportData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-7xl mx-auto"
            >
              {/* Header with Back Button */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
                <div className="text-center sm:text-left">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">{t("track_report.dashboard_title")}</h2>
                  <p className="text-sm sm:text-base text-gray-600">{t("track_report.tracking_id_prefix")} <span className="font-mono font-semibold">{reportData.tracking_id}</span></p>
                </div>
                <button
                  onClick={() => {
                    setReportData(null);
                    setTrackingId('');
                    setError('');
                    localStorage.removeItem('trackingData');
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium self-center sm:self-auto"
                >
                  {t("track_report.new_search")}
                </button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Status Overview */}
                <div className="lg:col-span-3 space-y-6">
                  {/* Status Alert for Attempt */}
                  {reportData.status_code === 'requires_action' && (
                    <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl shadow-lg p-6 text-white mb-6">
                      <div className="flex items-center mb-4">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-2xl mr-3" />
                        <h3 className="text-xl font-bold">{t("track_report.attempt_title")}</h3>
                      </div>
                      <p className="text-white/90 mb-4">
                        {t("track_report.attempt_description")}
                      </p>
                      {reportData.notes && (
                        <div className="bg-white/10 rounded-lg p-4 mb-4">
                          <h4 className="font-semibold mb-2">{t("track_report.reason_label")}</h4>
                          <p className="text-white/90">{reportData.notes}</p>
                        </div>
                      )}
                      <div className="flex items-center text-sm text-white/80">
                        <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                        <span>{t("track_report.check_email")}</span>
                      </div>
                    </div>
                  )}

                  {/* Report Overview Card */}
                  <div className="bg-gradient-to-r from-primary to-shake rounded-2xl shadow-lg p-8 text-white">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-2xl font-bold flex items-center">
                        <div className="bg-white/20 p-3 rounded-xl mr-4">
                          <FontAwesomeIcon icon={getReportTypeIcon(reportData.report_type)} className="text-white text-xl" />
                        </div>
                        {t("track_report.report_overview")}
                      </h3>
                      <button
                        onClick={handleRefresh}
                        disabled={isLoading}
                        className="bg-white/20 hover:bg-white/30 transition-colors p-3 rounded-xl disabled:opacity-50"
                        title={t("track_report.refresh_status_title")}
                      >
                        <FontAwesomeIcon icon={faRefresh} className={`text-white ${isLoading ? 'animate-spin' : ''}`} />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20 col-span-1 sm:col-span-2 lg:col-span-1">
                        <div className="text-xs sm:text-sm font-bold mb-3 font-mono tracking-wider overflow-hidden">{reportData.tracking_id}</div>
                        <div className="text-white/80 text-xs sm:text-sm font-medium">{t("track_report.tracking_id_label")}</div>
                      </div>
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
                        <div className="inline-block px-3 py-2 rounded-full text-sm sm:text-base font-bold bg-white/30 backdrop-blur-sm mb-3">
                          {getDisplayStatus(reportData.status)}
                        </div>
                        <div className="text-white/80 text-xs sm:text-sm font-medium">{t("track_report.current_status")}</div>
                      </div>
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
                        <div className="text-lg sm:text-xl font-bold mb-3">{reportData.report_type}</div>
                        <div className="text-white/80 text-xs sm:text-sm font-medium">{t("track_report.report_type")}</div>
                      </div>
                    </div>

                    <div className="border-t border-white/20 pt-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex items-center justify-center md:justify-start">
                          <FontAwesomeIcon icon={faCalendarAlt} className="text-white/80 mr-3" />
                          <div>
                            <div className="text-white/80 text-xs font-medium uppercase tracking-wide">{t("track_report.submitted")}</div>
                            <div className="text-white font-semibold">{formatDate(reportData.submitted_date)}</div>
                          </div>
                        </div>
                        <div className="flex items-center justify-center md:justify-start">
                          <FontAwesomeIcon icon={faClock} className="text-white/80 mr-3" />
                          <div>
                            <div className="text-white/80 text-xs font-medium uppercase tracking-wide">{t("track_report.last_updated")}</div>
                            <div className="text-white font-semibold">{formatDate(reportData.last_updated)}</div>
                          </div>
                        </div>
                        <div className="flex items-center justify-center md:justify-start">
                          <FontAwesomeIcon icon={faUser} className="text-white/80 mr-3" />
                          <div>
                            <div className="text-white/80 text-xs font-medium uppercase tracking-wide">{t("track_report.reporter")}</div>
                            <div className="text-white font-semibold">{reportData.reporter_name}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status Notes */}
                  {reportData.notes && (
                    <div className="bg-white rounded-2xl shadow-lg p-6">
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-3 text-primary" />
                        {t("track_report.latest_update")}
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-gray-700">{reportData.notes}</p>
                        <div className="text-sm text-gray-500 mt-2">
                          <FontAwesomeIcon icon={faClock} className="mr-1" />
                          {t("track_report.updated_prefix")} {formatDate(reportData.last_updated)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Progress Timeline */}
                  <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6 sm:mb-8 flex items-center">
                      <div className="bg-primary/10 p-2 sm:p-3 rounded-xl mr-3">
                        <FontAwesomeIcon icon={faTimeline} className="text-primary text-lg sm:text-xl" />
                      </div>
                      {t("track_report.report_timeline")}
                    </h3>

                    <div className="relative">
                      {/* Timeline Line */}
                      <div className="absolute left-4 sm:left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                      <div className="space-y-6 sm:space-y-8">
                        {getTimelineSteps(reportData.status_code, reportData.submitted_date, reportData.last_updated).map((step, index) => (
                          <div key={index} className="relative flex items-start">
                            <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mr-4 sm:mr-6 flex-shrink-0 border-4 border-white shadow-lg z-10 ${
                              step.status === 'completed' ? 'bg-green-500' :
                              step.status === 'current' ? 'bg-blue-500' : 'bg-gray-300'
                            }`}>
                              {step.status === 'current' ? (
                                <InlineSpinner size="sm" className="border-white/30 border-t-white" />
                              ) : (
                                <FontAwesomeIcon
                                  icon={step.status === 'completed' ? faCheckCircle : faClock}
                                  className="text-white text-xs sm:text-base"
                                />
                              )}
                            </div>
                            <div className="flex-1 min-w-0 pb-2">
                              <div className={`text-base sm:text-lg font-semibold mb-1 ${
                                step.status === 'completed' ? 'text-green-700' :
                                step.status === 'current' ? 'text-blue-700' : 'text-gray-500'
                              }`}>
                                {step.label}
                              </div>
                              {step.date && (
                                <div className="text-xs sm:text-sm text-gray-500 flex items-center mb-1">
                                  <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                                  {formatDate(step.date)}
                                </div>
                              )}
                              {step.status === 'current' && (
                                <div className="text-xs sm:text-sm text-blue-600 font-medium">
                                  {t("track_report.team_working")}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Quick Stats */}
                  <div className="bg-gradient-to-r from-secondary to-midnigt rounded-2xl shadow-lg p-6 text-white">
                    <h3 className="text-lg font-bold mb-4 flex items-center">
                      <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                      {t("track_report.quick_info")}
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-white/80">{t("track_report.days_since_submitted")}</span>
                        <span className="font-bold">{Math.ceil((new Date() - new Date(reportData.submitted_date)) / (1000 * 60 * 60 * 24))}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/80">{t("track_report.report_type")}:</span>
                        <span className="font-bold">{reportData.report_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/80">{t("track_report.priority")}:</span>
                        <span className="font-bold">{t("track_report.priority_normal")}</span>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="bg-white rounded-2xl shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                      <FontAwesomeIcon icon={faPhone} className="mr-2 text-primary" />
                      {t("track_report.need_help")}
                    </h3>
                    <div className="space-y-4">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center mb-1">
                          <FontAwesomeIcon icon={faEnvelope} className="text-primary mr-2" />
                          <span className="font-medium text-gray-800">{t("track_report.email_support")}</span>
                        </div>
                        <span className="text-sm text-gray-600">ethiocert@insa.gov.et</span><br />
                        <span className="text-sm text-gray-600">incident_response@insa.gov.et</span>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center mb-1">
                          <FontAwesomeIcon icon={faPhone} className="text-primary mr-2" />
                          <span className="font-medium text-gray-800">{t("track_report.emergency_contact")}</span>
                        </div>
                        <span className="text-sm text-gray-600">933</span>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center mb-1">
                          <FontAwesomeIcon icon={faClock} className="text-primary mr-2" />
                          <span className="font-medium text-gray-800">{t("track_report.office_hours")}</span>
                        </div>
                        <span className="text-sm text-gray-600">{t("track_report.office_hours_24_7")}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="bg-white rounded-2xl shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">{t("track_report.actions")}</h3>
                    <div className="space-y-3">
                      {/* <button
                        onClick={() => window.print()}
                        className="w-full bg-gray-100 hover:bg-gray-200 px-4 py-3 rounded-lg transition-colors flex items-center justify-center font-medium"
                      >
                        <FontAwesomeIcon icon={faPrint} className="mr-2" />
                        Print Report
                      </button> */}
                      <button
                        onClick={handleRefresh}
                        disabled={isLoading}
                        className="w-full bg-primary hover:bg-primary-dark text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center font-medium disabled:opacity-50"
                      >
                        <FontAwesomeIcon icon={faRefresh} className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                        {t("track_report.refresh_status")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
      <Toaster position="top-right" />
    </>
  );
};

export default TrackReport;